import { paths } from '@app/routes/paths';
import { features } from '@config/features';
import { Role } from '@features/auth';
import { SessionExpiryWarning } from '@features/auth/components/SessionExpiryWarning';
import { useAuth } from '@features/auth/hooks/useAuth';
import { tDynamic } from '@i18n/helpers';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PeopleIcon from '@mui/icons-material/PeopleAltOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import { AnalyticsRouteObserver } from '@services/analytics/AnalyticsRouteObserver';
import { CommandPalette } from '@shared/components/CommandPalette';
import { OfflineBanner } from '@shared/components/feedback/OfflineBanner';
import { AppBreadcrumbs } from '@shared/components/layout/AppBreadcrumbs';
import { DocumentTitle } from '@shared/components/layout/DocumentTitle';
import { fadeIn, useMotionSafe } from '@shared/components/motion';
import { UserMenu } from '@shared/components/UserMenu';
import { useIsPhone } from '@shared/hooks/useIsPhone';
import { tokens } from '@theme/tokens';
import { motion } from 'framer-motion';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { AppLogo } from './AppLogo';
import { AppSidebar } from './AppSidebar';
import {
  MOBILE_DRAWER_WIDTH,
  RAIL_HOVER,
  RAIL_TEXT,
  RAIL_TEXT_DIM,
  SIDEBAR_WIDTH,
} from './appSidebarLayout';
import { PhoneGatedOutlet } from './PhoneGatedOutlet';
import { usePhoneGate } from './usePhoneGate';

interface NavItem {
  labelKey: string;
  path: string;
  icon: ReactNode;
  requiredRole?: Role;
  /** Usable on a phone; every other entry is marked "Desktop" there (see `usePhoneGate`). */
  mobileReady?: boolean;
}

const navItems: readonly NavItem[] = [
  {
    labelKey: 'nav.dashboard',
    path: paths.dashboard,
    icon: <DashboardIcon />,
    requiredRole: Role.Admin,
  },
  { labelKey: 'nav.users', path: paths.users, icon: <PeopleIcon />, requiredRole: Role.Admin },
  {
    labelKey: 'nav.clients',
    path: paths.clients,
    icon: <BusinessIcon />,
    requiredRole: Role.Admin,
  },
  {
    labelKey: 'nav.clients',
    path: paths.projectClients,
    icon: <BusinessIcon />,
    requiredRole: Role.Tenant,
  },
  {
    labelKey: 'nav.priceCatalog',
    path: paths.priceCatalog,
    icon: <MenuBookOutlinedIcon />,
    requiredRole: Role.Tenant,
    mobileReady: true,
  },
  {
    labelKey: 'nav.doorTemplates',
    path: paths.doorTemplates,
    icon: <DashboardCustomizeOutlinedIcon />,
    requiredRole: Role.Tenant,
  },
  {
    labelKey: 'nav.doorRules',
    path: paths.doorRules,
    icon: <RuleOutlinedIcon />,
    requiredRole: Role.Tenant,
  },
  {
    labelKey: 'nav.partners',
    path: paths.partners,
    icon: <HandshakeOutlinedIcon />,
    requiredRole: Role.Admin,
  },
  ...(features.pricingPackages
    ? [
        {
          labelKey: 'nav.pricingPackages',
          path: paths.pricingPackages,
          icon: <LocalOfferOutlinedIcon />,
          requiredRole: Role.Tenant,
        },
      ]
    : []),
];

export function AppShell() {
  const { hasRole } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const animateSafe = useMotionSafe();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isPhone = useIsPhone();
  const { desktopOnly } = usePhoneGate();

  const visibleItems = navItems.filter(
    (item) => !item.requiredRole || hasRole([item.requiredRole]),
  );

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  // Landing pages and self-headed tools carry their own header — a lone
  // breadcrumb above them is noise (Preiskatalog renders its own overline).
  const isLandingPage =
    location.pathname === paths.dashboard ||
    location.pathname === paths.projectClients ||
    location.pathname === paths.doorTemplates ||
    location.pathname === paths.doorRules ||
    location.pathname.startsWith(paths.priceCatalog);

  const mobileDrawerContent = (
    <>
      <Toolbar sx={{ px: 2, color: RAIL_TEXT }}>
        <AppLogo />
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)' }} />
      <List sx={{ px: 1.5, py: 1.5, flex: 1, overflow: 'auto' }}>
        {visibleItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={isActive(item.path)}
              onClick={() => {
                setMobileOpen(false);
              }}
              sx={{
                borderRadius: 999,
                py: 1.25,
                color: isActive(item.path) ? '#ffffff' : RAIL_TEXT_DIM,
                '&:hover': { backgroundColor: RAIL_HOVER, color: RAIL_TEXT },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={tDynamic(t, item.labelKey)} />
              {isPhone && !item.mobileReady && (
                // The entry stays, so the page can be found; it says where it opens.
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 1,
                    borderRadius: 999,
                    border: `1px solid ${RAIL_TEXT_DIM}`,
                    fontSize: '0.6875rem',
                    lineHeight: 1.6,
                    color: RAIL_TEXT_DIM,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('nav.desktopOnlyTag')}
                </Box>
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)' }} />
      <Box sx={{ p: 1 }}>
        <UserMenu variant="row" />
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        id="main-navigation"
        aria-label={t('nav.mainNavigation')}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
        }}
      >
        <AppSidebar items={visibleItems} isActive={isActive} />
      </Box>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => {
          setMobileOpen(false);
        }}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: MOBILE_DRAWER_WIDTH,
            boxSizing: 'border-box',
            background: tokens.gradient.rail,
            color: RAIL_TEXT,
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {mobileDrawerContent}
      </Drawer>

      {/* Mobile nav trigger — floating machined key (the header is gone). */}
      <IconButton
        aria-label={t('nav.openNavigation')}
        onClick={() => {
          setMobileOpen(true);
        }}
        sx={(theme) => ({
          position: 'fixed',
          top: 12,
          left: 12,
          zIndex: theme.zIndex.appBar,
          width: 44,
          height: 44,
          borderRadius: 3,
          bgcolor: 'background.paper',
          boxShadow: tokens.shadow.float,
          ...theme.applyStyles('dark', { boxShadow: tokens.shadow.darkFloat }),
          display: { xs: 'inline-flex', md: 'none' },
        })}
      >
        <MenuIcon />
      </IconButton>

      <Box
        component="main"
        sx={(theme) => ({
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: 'background.default',
          // Drafting-table backdrop: a faint technical grid with workshop
          // light falling from the top — same family as the plan's light
          // table, so every page sits on machined paper.
          backgroundImage: `
            linear-gradient(to right, rgba(15,38,38,0.045) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(15,38,38,0.045) 1px, transparent 1px),
            radial-gradient(ellipse 90% 100% at 50% 0%, rgba(52,115,115,0.10) 0%, rgba(52,115,115,0.035) 45%, transparent 75%)
          `,
          backgroundSize: '40px 40px, 40px 40px, 100% 480px',
          backgroundRepeat: 'repeat, repeat, no-repeat',
          ...theme.applyStyles('dark', {
            backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px),
              radial-gradient(ellipse 90% 100% at 50% 0%, rgba(126,186,186,0.07) 0%, rgba(126,186,186,0.025) 45%, transparent 75%)
            `,
          }),
        })}
      >
        <OfflineBanner />
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            pt: { xs: 9, md: 4 },
            pb: 3,
            px: { xs: 2, sm: 3, md: 4 },
            flexGrow: 1,
            width: '100%',
          }}
        >
          <DocumentTitle />
          {!isLandingPage && !desktopOnly && (
            // Breadcrumb row with a page-actions slot: pages portal compact
            // machined keys (e.g. Offerte) onto the trail's flex line.
            <Box
              sx={{
                position: 'relative',
                mb: 2,
                // The trail alone defines the row height — identical position
                // and size to the page-local trail on /projects. Portalled
                // action keys hang centered on that line without growing it.
              }}
            >
              <AppBreadcrumbs />
              <Box
                id="breadcrumb-actions"
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              />
            </Box>
          )}
          {/* Quiet cross-fade between routes (motion map: Navigation). */}
          <motion.div
            key={location.pathname}
            variants={fadeIn}
            initial={animateSafe ? 'hidden' : false}
            animate="show"
          >
            <PhoneGatedOutlet />
          </motion.div>
        </Container>
      </Box>
      <SessionExpiryWarning />
      <CommandPalette />
      <AnalyticsRouteObserver />
    </Box>
  );
}
